import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getPriceService, usePrices } from '@hooks/usePrices';
import { brandColors } from '@utils/color-utils';
import './landing.css';

export const LandingScreen = () => {
  const { data: prices } = usePrices();
  const previousPrices = useRef<Map<string, number>>(new Map());
  const [priceColors, setPriceColors] = useState<Map<string, string>>(
    new Map(),
  );
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // Ref for ticker animation
  const tickerRef = useRef<HTMLDivElement>(null);
  const [tickerWidth, setTickerWidth] = useState(0);

  // Load Tally embed script
  useEffect(() => {
    const script = document.createElement('script');
    script.innerHTML = `
      var d=document,w="https://tally.so/widgets/embed.js",v=function(){"undefined"!=typeof Tally?Tally.loadEmbeds():d.querySelectorAll("iframe[data-tally-src]:not([src])").forEach((function(e){e.src=e.dataset.tallySrc}))};if("undefined"!=typeof Tally)v();else if(d.querySelector('script[src="'+w+'"]')==null){var s=d.createElement("script");s.src=w,s.onload=v,s.onerror=v,d.body.appendChild(s);}
    `;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector(
        'script[src="https://tally.so/widgets/embed.js"]',
      );
      if (existingScript) {
        existingScript.remove();
      }
      document.body.removeChild(script);
    };
  }, []);

  // Bootstrap all crypto prices for the ticker
  useEffect(() => {
    const priceService = getPriceService();
    priceService.bootstrap(['BTC', 'ETH', 'SOL', 'XRP']);
  }, []);

  // Measure ticker width for perfect animation
  useEffect(() => {
    const measureTickerWidth = () => {
      if (tickerRef.current) {
        const firstChild = tickerRef.current.firstElementChild as HTMLElement;
        if (firstChild) {
          setTickerWidth(firstChild.offsetWidth);
        }
      }
    };

    // Measure after prices load and on resize
    measureTickerWidth();
    window.addEventListener('resize', measureTickerWidth);

    // Re-measure when prices change (content width might change)
    const timer = setTimeout(measureTickerWidth, 1000);

    return () => {
      window.removeEventListener('resize', measureTickerWidth);
      clearTimeout(timer);
    };
  }, [prices]);

  // Track price changes and update colors
  useEffect(() => {
    if (!prices || typeof prices.get !== 'function') {
      return;
    }

    const symbols: ('BTC' | 'ETH' | 'SOL' | 'XRP')[] = [
      'BTC',
      'ETH',
      'SOL',
      // 'XRP',
    ];
    symbols.forEach(symbol => {
      const currentPriceData = prices.get(symbol);
      if (!currentPriceData) {
        return;
      }

      const currentPrice = currentPriceData.price;
      const previousPrice = previousPrices.current.get(symbol);

      if (previousPrice === undefined) {
        // First time seeing this price, just store it (color stays default gray)
        previousPrices.current.set(symbol, currentPrice);
        return;
      }

      if (previousPrice !== currentPrice) {
        // Price changed, determine color and keep it
        const newColor =
          currentPrice > previousPrice ? brandColors.green : brandColors.red;

        setPriceColors(prev => {
          const newMap = new Map(prev);
          newMap.set(symbol, newColor);
          return newMap;
        });

        // Update previous price
        previousPrices.current.set(symbol, currentPrice);
      }
      // If price hasn't changed, keep existing color (no update needed)
    });
  }, [prices]);

  // Try app CTA removed for now to keep users on landing

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const scrollToSignup = () => {
    const signupSection = document.querySelector('[data-signup-form]');
    if (signupSection) {
      signupSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Format price with exactly 2 decimal places
  const formatPrice = (price: number) => {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get price change direction for color coding
  const getPriceChangeColor = (symbol: 'BTC' | 'ETH' | 'SOL' | 'XRP') => {
    return priceColors.get(symbol) ?? '#9CA3AF'; // default to gray
  };

  // Get price for a symbol or return loading state
  const getDisplayPrice = useCallback(
    (symbol: 'BTC' | 'ETH' | 'SOL' | 'XRP') => {
      if (!prices || typeof prices.get !== 'function') {
        return '--';
      }
      const priceData = prices.get(symbol);
      return priceData ? formatPrice(priceData.price) : '--';
    },
    [prices],
  );

  // Create ticker content - simple and stable

  // Different order for mobile ticker
  const mobileCryptos = [
    // { symbol: 'XRP' as const, name: 'XRP' },
    { symbol: 'BTC' as const, name: 'BTC' },
    { symbol: 'ETH' as const, name: 'ETH' },
    { symbol: 'SOL' as const, name: 'SOL' },
  ];

  return (
    <div className="min-h-screen bg-[#030303] text-white overflow-x-hidden">
      {/* Header */}
      <header className="relative z-10 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Desktop Layout */}
          <div className="hidden md:flex justify-between items-center">
            <img src="/yolologo.svg" alt="YOLO logo" className="h-8 w-auto" />

            {/* Real-time Price Ticker */}
            <div className="flex items-center gap-6 text-sm font-medium">
              {[
                { symbol: 'BTC' as const, name: 'BTC' },
                { symbol: 'ETH' as const, name: 'ETH' },
                { symbol: 'SOL' as const, name: 'SOL' },
                // { symbol: 'XRP' as const, name: 'XRP' },
              ].map(crypto => (
                <div key={crypto.symbol} className="flex items-center gap-2">
                  <span className="text-gray-400">{crypto.name}</span>
                  <span
                    className="font-mono transition-colors duration-200"
                    style={{ color: getPriceChangeColor(crypto.symbol) }}
                  >
                    {getDisplayPrice(crypto.symbol)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden space-y-4">
            {/* Centered Logo */}
            <div className="flex justify-center">
              <img src="/yolologo.svg" alt="YOLO logo" className="h-8 w-auto" />
            </div>

            {/* Scrolling Price Ticker Below Logo */}
            <div className="w-full overflow-hidden relative">
              <div
                ref={tickerRef}
                className="flex whitespace-nowrap"
                style={
                  {
                    animation:
                      tickerWidth > 0
                        ? 'ticker-scroll 15s linear infinite'
                        : 'none',
                    '--ticker-width': `${tickerWidth}px`,
                  } as React.CSSProperties & { '--ticker-width': string }
                }
              >
                {/* First set of items */}
                <div className="flex">
                  {mobileCryptos.map(crypto => (
                    <div
                      key={`first-${crypto.symbol}`}
                      className="flex items-center gap-2 mx-6 whitespace-nowrap text-sm font-medium"
                    >
                      <span className="text-gray-400">{crypto.name}</span>
                      <span
                        className="font-mono transition-colors duration-200"
                        style={{ color: getPriceChangeColor(crypto.symbol) }}
                      >
                        {getDisplayPrice(crypto.symbol)}
                      </span>
                    </div>
                  ))}
                </div>
                {/* Second set of items (for seamless loop) */}
                <div className="flex">
                  {mobileCryptos.map(crypto => (
                    <div
                      key={`second-${crypto.symbol}`}
                      className="flex items-center gap-2 mx-6 whitespace-nowrap text-sm font-medium"
                    >
                      <span className="text-gray-400">{crypto.name}</span>
                      <span
                        className="font-mono transition-colors duration-200"
                        style={{ color: getPriceChangeColor(crypto.symbol) }}
                      >
                        {getDisplayPrice(crypto.symbol)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 px-6 py-4 md:py-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center">
            {/* Left Column - Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8 text-center"
            >
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                Turn Daily Volatility Into Opportunity
              </h1>

              <p className="text-xl text-gray-300 leading-relaxed">
                Amplify daily price action on majors.
                <br />
                High-risk trades, zero liquidations.
              </p>

              <div className="space-y-6" data-signup-form>
                <h3 className="text-2xl font-semibold">Start Trading</h3>

                <div className="rounded-lg">
                  <iframe
                    data-tally-src="https://tally.so/embed/wQbOBX?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1&formEventsForwarding=1"
                    loading="lazy"
                    width="100%"
                    height="172"
                    frameBorder="0"
                    marginHeight={0}
                    marginWidth={0}
                    title="Early Access Signup"
                    className="rounded-lg"
                  />
                </div>
              </div>
            </motion.div>

            {/* Right Column - iPhone Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center"
            >
              <div className="relative">
                {/* iPhone 14 Frame - Responsive dimensions */}
                <div className="relative w-[281px] h-[609px] md:w-[375px] md:h-[812px]">
                  {/* iPhone Outer Frame */}
                  <div className="absolute inset-0 bg-gradient-to-b from-gray-200 to-gray-400 rounded-[45px] md:rounded-[60px] shadow-2xl">
                    {/* iPhone Inner Frame */}
                    <div className="absolute inset-[3px] md:inset-[4px] bg-[#0D0D0E] rounded-[42px] md:rounded-[56px]">
                      {/* Screen Area */}
                      <div className="absolute inset-[4.5px] md:inset-[6px] bg-[#0D0D0E] rounded-[37.5px] md:rounded-[50px] overflow-hidden">
                        {/* Dynamic Island */}
                        <div className="absolute top-2 md:top-3 left-1/2 transform -translate-x-1/2 w-24 md:w-32 h-6 md:h-8 bg-[#0D0D0E] rounded-full z-20 border border-gray-700"></div>

                        {/* Status Bar Area */}
                        <div className="absolute top-0 left-0 right-0 h-10 md:h-14 bg-transparent z-10"></div>

                        {/* Live App Preview - Full screen with proper scaling */}
                        <div className="w-full h-full relative">
                          <img
                            src="/img/screenshot1.png"
                            className="w-full h-full pt-12 pb-6 border-none bg-[#0D0D0E]"
                            alt="YOLO Vibe App Preview"
                          />

                          {/* Transparent interaction blocker */}
                          <div
                            className="absolute inset-0 z-30 cursor-default"
                            title="Live preview - use buttons below to try the full app"
                          />
                        </div>

                        {/* Home Indicator */}
                        <div className="absolute bottom-1.5 md:bottom-2 left-1/2 transform -translate-x-1/2 w-27 md:w-36 h-1 md:h-1.5 bg-white/80 rounded-full z-20"></div>
                      </div>
                    </div>

                    {/* Side Buttons - iPhone 14 positioning */}
                    <div className="absolute left-[-2px] md:left-[-3px] top-[135px] md:top-[180px] w-1 md:w-1.5 h-9 md:h-12 bg-gray-300 rounded-l-sm"></div>
                    <div className="absolute left-[-2px] md:left-[-3px] top-[165px] md:top-[220px] w-1 md:w-1.5 h-12 md:h-16 bg-gray-300 rounded-l-sm"></div>
                    <div className="absolute left-[-2px] md:left-[-3px] top-[195px] md:top-[260px] w-1 md:w-1.5 h-12 md:h-16 bg-gray-300 rounded-l-sm"></div>
                    <div className="absolute right-[-2px] md:right-[-3px] top-[150px] md:top-[200px] w-1 md:w-1.5 h-15 md:h-20 bg-gray-300 rounded-r-sm"></div>
                  </div>

                  {/* Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-[45px] md:rounded-[60px] blur-xl -z-10"></div>

                  {/* Reflection Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-[60px] pointer-events-none"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 px-6 py-16 bg-gray-950/70 border-y-2 border-[#151515]">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-12"
          >
            Why YOLO
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Asymmetric upside',
                description:
                  'Losses are limited to your initial purchase amount. Profits are unlimited.',
                icon: (
                  <svg
                    width="129"
                    height="128"
                    viewBox="0 0 129 128"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M58.207 102.745C66.597 102.745 77.927 99.0649 86.077 84.9349C94.227 70.8049 111.477 25.2549 111.477 25.2549"
                      stroke="#00FF18"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M78.2373 57.0749L91.7773 25.2549"
                      stroke="white"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M17.8574 102.745H57.1774"
                      stroke="#FF0000"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M77.5176 31.1149L91.7776 25.2549L97.6376 39.5249"
                      stroke="white"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                  </svg>
                ),
              },
              {
                title: 'No liquidation risk',
                description:
                  'Crypto options provide extreme volaility without the downside of leverage.',
                icon: (
                  <svg
                    width="128"
                    height="128"
                    viewBox="0 0 128 128"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M57.282 27.6919L27.332 79.5619C23.222 86.6719 28.362 95.5719 36.572 95.5719H96.472C104.682 95.5719 109.822 86.6819 105.712 79.5619L75.762 27.6919C71.652 20.5819 61.392 20.5819 57.282 27.6919Z"
                      stroke="white"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M61.7217 62.1312C61.7217 64.7812 63.8717 66.9313 66.5217 66.9313C69.1717 66.9313 71.3217 64.7812 71.3217 62.1312V46.5812C71.3217 43.9312 69.1717 41.7812 66.5217 41.7812C63.8717 41.7812 61.7217 43.9312 61.7217 46.5812V62.1312Z"
                      stroke="white"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M66.5217 82.4818C69.1726 82.4818 71.3217 80.3328 71.3217 77.6818C71.3217 75.0309 69.1726 72.8818 66.5217 72.8818C63.8707 72.8818 61.7217 75.0309 61.7217 77.6818C61.7217 80.3328 63.8707 82.4818 66.5217 82.4818Z"
                      stroke="white"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M21.9922 105.641C21.9922 105.641 83.5622 44.0713 100.272 27.3613"
                      stroke="black"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M20.8418 104.192C20.8418 104.192 82.4018 42.6319 99.1118 25.9219"
                      stroke="#FF0000"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                  </svg>
                ),
              },
              {
                title: 'Deep liquidity',
                description:
                  'Open and close positions confidently any time of day. Crypto never sleeps.',
                icon: (
                  <svg
                    width="129"
                    height="128"
                    viewBox="0 0 129 128"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M107.309 35.75C102.559 31 94.8694 31 90.1194 35.75C85.3694 40.5 77.6794 40.5 72.9294 35.75C68.1794 31 60.4894 31 55.7394 35.75C50.9894 40.5 43.2994 40.5 38.5494 35.75C33.7994 31 26.1094 31 21.3594 35.75"
                      stroke="white"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M107.309 49.875C102.559 45.125 94.8694 45.125 90.1194 49.875C85.3694 54.625 77.6794 54.625 72.9294 49.875C68.1794 45.125 60.4894 45.125 55.7394 49.875C50.9894 54.625 43.2994 54.625 38.5494 49.875C33.7994 45.125 26.1094 45.125 21.3594 49.875"
                      stroke="white"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M107.309 64C102.559 59.25 94.8694 59.25 90.1194 64C85.3694 68.75 77.6794 68.75 72.9294 64C68.1794 59.25 60.4894 59.25 55.7394 64C50.9894 68.75 43.2994 68.75 38.5494 64C33.7994 59.25 26.1094 59.25 21.3594 64"
                      stroke="white"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M107.309 78.125C102.559 73.375 94.8694 73.375 90.1194 78.125C85.3694 82.875 77.6794 82.875 72.9294 78.125C68.1794 73.375 60.4894 73.375 55.7394 78.125C50.9894 82.875 43.2994 82.875 38.5494 78.125C33.7994 73.375 26.1094 73.375 21.3594 78.125"
                      stroke="white"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M107.309 92.25C102.559 87.5 94.8694 87.5 90.1194 92.25C85.3694 97 77.6794 97 72.9294 92.25C68.1794 87.5 60.4894 87.5 55.7394 92.25C50.9894 97 43.2994 97 38.5494 92.25C33.7994 87.5 26.1094 87.5 21.3594 92.25"
                      stroke="white"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                  </svg>
                ),
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="p-6 bg-[#0D0D0E]/50 rounded-xl border border-gray-800 flex flex-col items-center text-center"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative z-10 px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-12"
          >
            Frequently Asked Questions
          </motion.h2>

          <div className="space-y-4">
            {[
              {
                question: 'How is Yolo different?',
                answer:
                  'Yolo makes trading zero-day crypto options simple and accessible. Unlike perps trading, there are no liquidations. Unlike memecoins, you’re trading major assets with deep liquidity. You get defined risk, high-upside potential, and an easy mobile-first experience built for same-day trading opportunities.',
              },
              {
                question: 'What are zero-day (0DTE) options?',
                answer:
                  'Zero-day options are contracts that expire the same day you buy them. They’ve gained popularity in traditional financial markets because they offer quick exposure to price volatility with a small upfront cost. That means you can potentially generate large returns in a single trading session, without tying up significant capital.',
              },
              {
                question: 'When can I start trading with Yolo?',
                answer:
                  'Yolo is launching soon. We’re currently in early access, gathering feedback from our initial group of traders before rolling out to the public. Sign up now to secure your spot and be the first to trade when we go live.',
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-800/30 transition-colors"
                >
                  <span className="text-lg font-medium">{faq.question}</span>
                  <motion.img
                    src="caret.svg"
                    alt="Toggle FAQ"
                    animate={{ rotate: openFAQ === index ? 0 : 180 }}
                    transition={{ duration: 0.2 }}
                    className="w-6 h-6 text-gray-400"
                  />
                </button>

                <AnimatePresence>
                  {openFAQ === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4 text-gray-300 leading-relaxed">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center mt-12"
          >
            <button
              onClick={scrollToSignup}
              className="text-white hover:opacity-90"
              style={{
                position: 'relative',
                cursor: 'pointer',
                transition:
                  'background-color 100ms ease-in-out, border-color 100ms ease-in-out, outline 100ms ease-in-out',
                lineHeight: '1',
                display: 'inline-flex',
                flexShrink: 0,
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                verticalAlign: 'middle',
                textDecoration: 'none',
                overflow: 'hidden',
                transform: 'translateZ(0px)',
                outline: 'none',
                outlineOffset: '-1px',
                height: '36px',
                fontSize: '15px',
                padding: '0px 0.9em',
                gap: '0.5em',
                borderRadius: '8px',
                border: '1px solid #00FF40',
                background: 'rgba(65, 255, 65, 0.20)',
                boxShadow:
                  '0 27px 80px 0 rgba(0, 0, 0, 0.07), 0 6.031px 17.869px 0 rgba(0, 0, 0, 0.04), 0 1.796px 5.32px 0 rgba(0, 0, 0, 0.03)',
              }}
            >
              Get early access
            </button>
          </motion.div>
        </div>
      </section>

      {/* Animated Gradient Background */}
      <div className="animated-gradient"></div>

      {/* Static Grid Background with Canvas Animation */}
      <div className="grid-background">
        <div className="grid-layer grid-small"></div>
        <div className="grid-layer grid-large"></div>
      </div>

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>
    </div>
  );
};
